import { useEffect, useState } from 'react';
import { Alert, Badge, Modal, Spinner } from 'react-bootstrap';
import { financeClient, assetUrl } from '../api/financeClient';
import { money } from '../format';

// Lightweight listing popup for finance id cells. Uses the public item endpoint
// (/api/:vertical/items/:id) via the module's own client. Admin gets an "Edit in dashboard" jump.
export default function ListingDetailsModal({ itemId, vertical, show, onHide, onOpenExternal, isAdmin }) {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !itemId || !vertical) return undefined;
    let cancelled = false;
    setItem(null); setError(''); setLoading(true);
    const path = vertical === 'real_estate'
      ? `/api/real_estate/properties/${itemId}`
      : `/api/${vertical}/items/${itemId}`;
    financeClient.get(path)
      .then((res) => { if (!cancelled) setItem(res.data?.data || res.data); })
      .catch(() => { if (!cancelled) setError('Could not load this listing.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [show, itemId, vertical]);

  return (
    <Modal show={show} onHide={onHide} centered fullscreen="sm-down">
      <Modal.Header closeButton>
        <Modal.Title as="h5">{item ? (item.name || item.title) : 'Listing details'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <Spinner animation="border" size="sm" role="status" />}
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
        {item && (
          <>
            <div className="mb-2">
              {item.status && <Badge bg={item.status === 'disabled' ? 'secondary' : 'success'}>{item.status}</Badge>}
              {item.category && <span className="ms-2 text-muted">{item.category}</span>}
            </div>
            {item.location && <p className="mb-1">{item.location}</p>}
            {(item.registrationNumber || item.attributes?.registrationNumber) && (
              <p className="mb-1">Plate: {item.registrationNumber || item.attributes.registrationNumber}</p>
            )}
            {item.price != null && <p className="mb-1"><strong>{money(item.price)}</strong>{item.priceUnit ? ` / ${item.priceUnit}` : ''}</p>}
            {(item.ownerName || item.ownerEmail) && (
              <p className="mb-1 small">Owner: {[item.ownerName, item.ownerEmail, item.ownerPhone].filter(Boolean).join(' · ')}</p>
            )}
            {item.description && <p className="mb-2 small">{item.description}</p>}
            {Array.isArray(item.images) && item.images.length > 0 && (
              <div className="d-flex flex-wrap gap-2">
                {item.images.slice(0, 6).map((src, i) => (
                  <img key={i} src={assetUrl(src)} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6 }} />
                ))}
              </div>
            )}
            {isAdmin && onOpenExternal && (
              <button type="button" className="btn btn-sm btn-outline-primary mt-3" onClick={() => onOpenExternal('listing', item)}>
                Edit in dashboard
              </button>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}
